import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../../api/client.js";
import type { PersonaSummary } from "../../../api/types.js";
import { RE_ERROR_TEXT } from "./re-api.js";

/**
 * FIX-011 / RE - approved decision: "save the RE plan for reuse, including with another persona". Reusing creates a NEW project for the chosen persona that carries over the approved analysis and the
 * proposals (selected / approved ones return as selected, awaiting approval 2 for the new persona and its voice); the analysis is not redone and the original project is never modified.
 */
export function ReReuse({ tenantId, projectId, currentPersonaId, csrfToken, canAct }: { readonly tenantId: string; readonly projectId: string; readonly currentPersonaId: string; readonly csrfToken: string | null; readonly canAct: boolean }) {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<readonly PersonaSummary[]>([]);
  const [choice, setChoice] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous in-flight guard: a second click before React re-renders (or a click on a stale button) never sends a second POST.
  const inFlight = useRef(false);

  // The page keeps this component mounted when it moves to the NEW project (same route, new id). Nothing from the previous project's choice may carry over:
  // a still-open "Yes, create the new project" with the old choice would copy the new project again (17:39 incident: reproj-3db76b5f copied from reproj-8569a79c).
  useEffect(() => {
    setChoice("");
    setConfirming(false);
    setError(null);
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    void api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`).then((r) => { if (alive) setPersonas(r.personas.filter((p) => p.personaId !== currentPersonaId)); }).catch(() => undefined);
    return () => { alive = false; };
  }, [tenantId, currentPersonaId]);

  if (!canAct || personas.length === 0) return null;

  const validChoice = choice !== "" && choice !== currentPersonaId && personas.some((p) => p.personaId === choice);

  async function reuse(): Promise<void> {
    if (inFlight.current || !validChoice) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ project: { id: string } }>(`/re/${tenantId}/projects/${encodeURIComponent(projectId)}/reuse`, csrfToken, { personaId: choice });
      // Success: the choice is cleared at once, so nothing can be sent again from the previous page while the new project loads.
      setConfirming(false);
      setChoice("");
      navigate(`/generate/create/re/projects/${encodeURIComponent(r.project.id)}`);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      setError((body?.error !== undefined ? RE_ERROR_TEXT[body.error] : undefined) ?? body?.message ?? "Could not reuse this plan.");
      setConfirming(false);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <section className="card" data-testid="re-reuse" style={{ marginTop: 12 }}>
      <h4 style={{ marginTop: 0 }}>Reuse this plan with another persona</h4>
      <p className="muted">Creates a new project for the persona you choose, with this approved analysis and these proposals. Nothing is analyzed again, and this project stays as it is. Selected proposals must be approved again for the new persona. The original persona's name and speaker labels in the proposals are switched to the new persona.</p>
      {error !== null ? <div className="error-banner" role="alert" data-testid="re-reuse-error">{error}</div> : null}
      <select data-testid="re-reuse-persona" value={choice} onChange={(e) => { setChoice(e.target.value); setConfirming(false); }}>
        <option value="">Choose a persona…</option>
        {personas.map((p) => <option key={p.personaId} value={p.personaId}>{p.displayName}</option>)}
      </select>{" "}
      {!confirming ? (
        <button type="button" data-testid="re-reuse-start" disabled={busy || !validChoice} onClick={() => setConfirming(true)}>Reuse this plan</button>
      ) : (
        <>
          <button type="button" data-testid="re-reuse-yes" disabled={busy || !validChoice} onClick={() => void reuse()}>{busy ? "Creating…" : "Yes, create the new project"}</button>{" "}
          <button type="button" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
        </>
      )}
    </section>
  );
}
